import React, { useState } from 'react';
import { 
  CreditCard, 
  Check, 
  Sparkles, 
  Download, 
  RefreshCw, 
  Zap, 
  ShieldCheck, 
  Lock, 
  AlertCircle, 
  AlertTriangle,
  Plus, 
  Trash2, 
  Calendar, 
  DollarSign, 
  CheckCircle2, 
  FileText, 
  Search, 
  Building, 
  Mail, 
  ShieldAlert,
  ArrowUpRight,
  ChevronRight,
  Clock,
  Layers,
  Award,
  Globe,
  ExternalLink,
  X,
  Loader2,
  ChevronDown
} from 'lucide-react';
import { DodoPayments } from 'dodopayments-checkout';
import { 
  SubscriptionData, 
  SubscriptionPlan, 
  PaymentMethod, 
  BillingHistoryItem, 
  AVAILABLE_PLANS, 
  getSubscriptionData, 
  saveSubscriptionData, 
  downloadSubscriptionInvoice,
  SUPPORTED_CURRENCIES,
  detectUserCurrency,
  getPlanPriceForCurrency,
  CurrencyTier,
  DEFAULT_SUBSCRIPTION_DATA
} from '../utils/subscriptionManager';
import { logAuditAction } from '../utils/auditLogger';
import { downloadFile } from './ImportExportCenter';
import { auth } from '../lib/firebase';
import { getUserTrialStatus, activateUserSubscription, deactivateUserSubscription, isUserSuperAdmin } from '../utils/userManagement';
import { getBusinessSettings, saveBusinessSettings } from '../utils/businessSettings';

interface SubscriptionBillingViewProps {
  showToast: (msg: string) => void;
  companyName?: string;
  onRefreshAuditLogs?: () => void;
  onNavigate?: (path: string) => void;
}

type BillingSectionTab = 'all' | 'plans' | 'receipts' | 'quotas';

interface BillingSectionItem {
  id: BillingSectionTab;
  title: string;
  shortLabel: string;
  desc: string;
  icon: React.ElementType;
  badge?: string;
  badgeColor?: string;
}

const BILLING_SECTIONS: BillingSectionItem[] = [
  {
    id: 'all',
    title: 'Complete Billing Overview',
    shortLabel: 'All Sections',
    desc: 'Display all plan pricing options, billing receipts, and legal guarantees together in one view.',
    icon: Layers,
  },
  {
    id: 'plans',
    title: 'Choose Your Sovereign Tier & Upgrades',
    shortLabel: 'Available Plans',
    desc: 'Compare Free Starter vs Sovereign Pro vs Enterprise, toggle monthly/yearly billing, and activate your tier.',
    icon: Award,
    badge: 'Popular',
    badgeColor: 'bg-[#14B8A6]/20 text-[#14B8A6] border-[#14B8A6]/40'
  },
  {
    id: 'receipts',
    title: 'Official Billing History & Tax Receipts',
    shortLabel: 'Billing Receipts',
    desc: 'Search past subscription payments, download itemized tax receipts, and export billing ledger to CSV.',
    icon: FileText,
    badge: 'Tax Compliant',
    badgeColor: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
  },
  {
    id: 'quotas',
    title: 'Resource Quotas & 14-Day Risk-Free Guarantee',
    shortLabel: 'Quotas & Guarantee',
    desc: 'Review workspace invoice limits, daily AI query capacity, AES-256 vault status, and refund terms.',
    icon: ShieldCheck,
    badge: 'Protected',
    badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
  }
];

function SubscriptionBillingViewComponent({ 
  showToast, 
  companyName,
  onRefreshAuditLogs,
  onNavigate
}: SubscriptionBillingViewProps) {
  const [activeBillingSection, setActiveBillingSection] = useState<BillingSectionTab>('all');
  const [isSectionDropdownOpen, setIsSectionDropdownOpen] = useState(false);
  const [subData, setSubData] = useState<SubscriptionData>(() => {
    const currentEmail = auth.currentUser?.email || '';
    return getSubscriptionData(currentEmail);
  });
  const [selectedCycle, setSelectedCycle] = useState<'monthly' | 'yearly'>(subData.billingCycle);
  const [selectedCurrency, setSelectedCurrency] = useState<string>(() => detectUserCurrency());
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<string>('all');
  
  // Dodo Payments State
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [activeCheckoutSession, setActiveCheckoutSession] = useState<{
    url: string;
    isLive: boolean;
    environment: 'live' | 'test';
    plan: SubscriptionPlan;
  } | null>(null);
  const [dodoNotice, setDodoNotice] = useState<string | null>(null);

  // Sync authenticated user email into billing email automatically without cross-account state pollution
  React.useEffect(() => {
    if (auth.currentUser?.email) {
      const email = auth.currentUser.email;
      const userSpecific = getSubscriptionData(email);
      setSubData(userSpecific);
    }
  }, [auth.currentUser?.email]);

  // Plan Switch Confirmation Modal State
  const [switchingPlan, setSwitchingPlan] = useState<SubscriptionPlan | null>(null);
  const [isSyncingDodo, setIsSyncingDodo] = useState(false);

  const userEmail = auth.currentUser?.email || subData.billingEmail;
  const trialStatus = getUserTrialStatus(userEmail);

  // Sync subscription status with live Dodo Payments API
  const handleSyncWithDodo = async (isManual = false) => {
    setIsSyncingDodo(true);
    try {
      const emailToQuery = userEmail || subData.billingEmail || '';
      if (!emailToQuery) {
        setIsSyncingDodo(false);
        if (isManual) {
          showToast('ℹ️ Please enter or sign in with your email to sync billing status.');
        }
        return;
      }
      const res = await fetch('/api/payments/sync-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail: emailToQuery,
          customerId: ''
        })
      });
      const contentType = res.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await res.json().catch(() => ({})) : {};

      if (data && data.success) {
        if (data.isSubscribed && data.subscription) {
          const sub = data.subscription;
          const now = new Date();
          const dateStr = sub.createdAt ? sub.createdAt.split('T')[0] : now.toISOString().split('T')[0];
          const nextBilling = sub.nextBillingDate ? sub.nextBillingDate.split('T')[0] : '2026-09-16';

          const updatedHistory = [...subData.history];
          if (sub.invoiceUrl && !updatedHistory.some(h => h.id === sub.id || h.id === sub.paymentId)) {
            updatedHistory.unshift({
              id: sub.paymentId || sub.id,
              invoiceNumber: `AZ-DODO-${sub.id.slice(-6).toUpperCase()}`,
              date: dateStr,
              description: `${sub.planName} - ${sub.billingCycle === 'yearly' ? 'Annual' : 'Monthly'} Subscription (Dodo Payments)`,
              amount: sub.amount || 29,
              currency: sub.currency || selectedCurrency,
              status: 'paid',
              paymentMethodLast4: 'SECURE'
            });
          }

          const updated: SubscriptionData = {
            ...subData,
            status: 'active',
            currentPlanId: sub.planId || 'pro',
            billingCycle: sub.billingCycle || 'monthly',
            billingEmail: sub.customerEmail || emailToQuery,
            billingName: sub.customerName || subData.billingName,
            nextBillingDate: nextBilling,
            history: updatedHistory,
            paymentMethods: []
          };

          setSubData(updated);
          saveSubscriptionData(updated, emailToQuery);
          activateUserSubscription(emailToQuery, sub.planId || 'pro', sub.billingCycle || 'monthly');

          if (isManual) {
            showToast('✅ Subscription verified with Dodo Payments! Sovereign Founder Pro is active.');
          }
        } else if (!data.isSubscribed) {
          // If not subscribed on Dodo and user is not master super-admin, ensure no phantom Pro access
          const isMaster = isUserSuperAdmin(emailToQuery);
          if (!isMaster && subData.status === 'active') {
            const resetData: SubscriptionData = {
              ...DEFAULT_SUBSCRIPTION_DATA,
              billingEmail: emailToQuery,
              status: 'trialing',
              history: []
            };
            setSubData(resetData);
            saveSubscriptionData(resetData, emailToQuery);
            deactivateUserSubscription(emailToQuery);
          }
          if (isManual) {
            showToast('ℹ️ Checked Dodo Payments: Workspace active with standard evaluation access.');
          }
        }
      } else if (isManual) {
        showToast('ℹ️ Checked Dodo Payments: Workspace active with standard evaluation access.');
      }
    } catch (err: any) {
      console.warn('Dodo live subscription sync notice:', err);
      if (isManual) {
        showToast('⚠️ Could not connect to Dodo Payments. Local access remains active.');
      }
    } finally {
      setIsSyncingDodo(false);
    }
  };

  // Handle Dodo payment redirect: verify with API first and immediately scrub URL parameters
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const paymentStatus = params.get('payment_status') || params.get('status');
      const hasPaymentParams = paymentStatus === 'success' || paymentStatus === 'succeeded' || !!params.get('payment_id') || !!params.get('subscription_id');

      if (hasPaymentParams) {
        // Immediately clean query params from URL bar cleanly to prevent link sharing or re-activation
        window.history.replaceState({}, '', window.location.pathname);

        // Verify with live Dodo Payments API before activating
        handleSyncWithDodo(true);
      } else {
        // Auto-sync in background on mount
        handleSyncWithDodo(false);
      }
    } catch (e) {
      console.warn('URL payment param check notice:', e);
      handleSyncWithDodo(false);
    }
  }, []);

  const isTrialActive = trialStatus.isTrialActive && !trialStatus.isSubscribed && subData.status !== 'active';

  const currentPlan = isTrialActive
    ? (AVAILABLE_PLANS.find(p => p.id === 'pro') || AVAILABLE_PLANS[1])
    : (AVAILABLE_PLANS.find(p => p.id === subData.currentPlanId) || AVAILABLE_PLANS[0]);

  const activeCurrencyInfo = SUPPORTED_CURRENCIES[selectedCurrency] || SUPPORTED_CURRENCIES.ZAR;

  const handleToggleAutoRenew = () => {
    const updated = { ...subData, autoRenew: !subData.autoRenew };
    setSubData(updated);
    saveSubscriptionData(updated);
    logAuditAction({
      actionType: 'UPDATE_SUBSCRIPTION',
      entityType: 'Settings',
      details: `Updated subscription auto-renewal to: ${updated.autoRenew ? 'ENABLED' : 'DISABLED'}`,
      performedBy: userEmail || 'workspace-founder'
    });
    if (onRefreshAuditLogs) onRefreshAuditLogs();
    showToast(updated.autoRenew ? '✅ Auto-renewal enabled.' : '⚠️ Auto-renewal disabled.');
  };

  const handleUpdateBillingEmail = (email: string) => {
    const updated = { ...subData, billingEmail: email };
    setSubData(updated);
    saveSubscriptionData(updated);
    showToast('📧 Subscription billing contact email updated.');
  };

  const getEffectiveCustomerName = () => {
    return (
      subData.billingName?.trim() ||
      companyName?.trim() ||
      getBusinessSettings().companyName?.trim() ||
      getBusinessSettings().senderName?.trim() ||
      auth.currentUser?.displayName?.trim() ||
      (userEmail ? userEmail.split('@')[0] : 'Workspace Account Holder')
    );
  };

  const handleOpenManageSubscription = async () => {
    if (subData.currentPlanId === 'starter' || subData.status !== 'active') {
      showToast('ℹ️ You are currently on the Free Starter plan. Upgrade to Pro or Enterprise to activate and manage recurring billing.');
      const plansSection = document.getElementById('subscription-plans-grid');
      if (plansSection) {
        plansSection.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }

    setIsPortalLoading(true);
    try {
      const res = await fetch('/api/payments/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail: userEmail || subData.billingEmail || '',
          customerName: getEffectiveCustomerName(),
          returnUrlOrigin: window.location.origin,
        }),
      });
      const contentType = res.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await res.json().catch(() => ({})) : {};
      if (data && data.success && data.portal_url) {
        showToast('🔒 Opening Dodo Payments secure portal for cancellations & billing updates...');
        window.open(data.portal_url, '_blank', 'noopener,noreferrer');
      } else {
        showToast(data?.message || '⚠️ Could not open subscription portal session.');
      }
    } catch (err: any) {
      console.error('Error launching Dodo customer portal session:', err);
      showToast('⚠️ Error connecting to subscription management portal.');
    } finally {
      setIsPortalLoading(false);
    }
  };

  const handleInitiateDodoCheckout = async (plan: SubscriptionPlan) => {
    if (plan.id === 'starter' || plan.monthlyPrice === 0) {
      completeSubscriptionActivation(plan, 0);
      return;
    }

    setIsCheckoutLoading(true);
    setDodoNotice(null);

    const priceInfo = getPlanPriceForCurrency(plan.id, selectedCycle, selectedCurrency);
    const resolvedName = getEffectiveCustomerName();

    try {
      const res = await fetch('/api/payments/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: plan.id,
          billingCycle: selectedCycle,
          customerEmail: userEmail || subData.billingEmail || '',
          customerName: resolvedName,
          billingTaxId: subData.billingTaxId || getBusinessSettings().taxNumber || '',
          billingAddress: subData.billingAddress || getBusinessSettings().address || '',
          currency: selectedCurrency,
          returnUrlOrigin: window.location.origin,
        }),
      });

      const contentType = res.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await res.json().catch(() => ({})) : {};

      if (!data || !data.success || !data.checkout_url) {
        throw new Error(data?.error || 'Could not generate Dodo checkout session URL.');
      }

      if (data.message) {
        setDodoNotice(data.message);
      }

      // If live mode, open Dodo hosted checkout page directly
      if (data.isLiveKey || data.environment === 'live') {
        window.open(data.checkout_url, '_blank', 'noopener,noreferrer');
        showToast('🔒 Opening Dodo Payments secure live checkout...');
      } else {
        // Trigger Dodo SDK Overlay Modal in test mode if available
        try {
          DodoPayments.Checkout.open({
            url: data.checkout_url,
            checkoutUrl: data.checkout_url,
            displayType: 'overlay',
            onSuccess: (paymentResult: any) => {
              console.log('Dodo checkout overlay success:', paymentResult);
              completeSubscriptionActivation(plan, priceInfo.amount, paymentResult);
            },
            onClose: () => {
              setIsCheckoutLoading(false);
            },
          } as any);
        } catch (sdkErr) {
          console.warn('DodoCheckout SDK notice:', sdkErr);
        }
      }

      // Set checkout session state for modal management
      setActiveCheckoutSession({
        url: data.checkout_url,
        isLive: !!data.isLiveKey,
        environment: (data.environment as 'live' | 'test') || (data.isLiveKey ? 'live' : 'test'),
        plan: plan,
      });
    } catch (err: any) {
      console.error('Failed to initiate Dodo Payments session:', err);
      showToast(`⚠️ Payment notice: ${err.message || 'Could not connect to Dodo Payments'}`);
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const completeSubscriptionActivation = (
    plan: SubscriptionPlan,
    priceAmount: number,
    paymentDetails?: any,
    shouldNavigate: boolean = true
  ) => {
    const isFree = plan.id === 'starter' || priceAmount === 0;
    const isAnnual = selectedCycle === 'yearly';
    const now = new Date();
    const renewalDate = new Date(now);
    if (isAnnual) {
      renewalDate.setFullYear(now.getFullYear() + 1);
    } else {
      renewalDate.setMonth(now.getMonth() + 1);
    }

    const dateStr = now.toISOString().split('T')[0];
    const renewalStr = renewalDate.toISOString().split('T')[0];

    const newInvoice: BillingHistoryItem = {
      id: paymentDetails?.payment_id || `inv_${Date.now()}`,
      invoiceNumber: `AZ-INV-${now.getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
      date: dateStr,
      description: isFree 
        ? `${plan.name} - Free Tier Activation` 
        : `${plan.name} - ${isAnnual ? 'Annual' : 'Monthly'} Subscription (Dodo Payments)`,
      amount: priceAmount,
      currency: selectedCurrency,
      status: 'paid',
      paymentMethodLast4: isFree ? 'FREE' : 'SECURE',
    };

    const updated: SubscriptionData = {
      ...subData,
      currentPlanId: plan.id,
      billingCycle: selectedCycle,
      status: 'active',
      startDate: dateStr,
      nextBillingDate: isFree ? 'N/A (Free Forever)' : renewalStr,
      history: [newInvoice, ...subData.history],
    };

    setSubData(updated);
    saveSubscriptionData(updated);

    logAuditAction({
      actionType: 'PLAN_CHANGE',
      entityType: 'Settings',
      details: isFree
        ? `Activated Free Tier plan "${plan.name}"`
        : `Activated plan "${plan.name}" via Dodo Payments (${selectedCurrency} ${priceAmount})`,
      performedBy: userEmail || 'workspace-founder',
    });
    if (onRefreshAuditLogs) onRefreshAuditLogs();

    if (!isFree && userEmail) {
      activateUserSubscription(userEmail);
    }

    setSwitchingPlan(null);
    setActiveCheckoutSession(null);
    setIsCheckoutLoading(false);
    showToast(isFree ? `🎉 Plan switched to ${plan.name}!` : `🎉 Subscription updated to ${plan.name} via Dodo Payments! Invoice generated.`);
    
    if (shouldNavigate && onNavigate) {
      onNavigate('/welcome');
    }
  };

  const handleConfirmPlanSwitch = () => {
    if (!switchingPlan) return;
    if (switchingPlan.id === 'starter' || switchingPlan.monthlyPrice === 0) {
      completeSubscriptionActivation(switchingPlan, 0);
      return;
    }
    const priceInfo = getPlanPriceForCurrency(switchingPlan.id, selectedCycle, selectedCurrency);
    handleInitiateDodoCheckout(switchingPlan);
  };

  const handleExportBillingHistoryCSV = () => {
    if (subData.history.length === 0) {
      showToast('⚠️ No billing history entries available.');
      return;
    }
    const headers = ['Invoice Number', 'Date', 'Description', 'Amount (USD)', 'Status', 'Payment Method'];
    const rows = subData.history.map(item => [
      item.invoiceNumber,
      item.date,
      `"${item.description.replace(/"/g, '""')}"`,
      item.amount.toFixed(2),
      item.status.toUpperCase(),
      'Secured Electronic Checkout'
    ]);
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    downloadFile(csvContent, `azuraos_billing_history_${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
    showToast('📥 Billing history exported to CSV.');
  };

  const filteredHistory = subData.history.filter(item => {
    const matchesSearch = 
      item.invoiceNumber.toLowerCase().includes(historySearchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(historySearchQuery.toLowerCase());
    const matchesStatus = historyStatusFilter === 'all' || item.status === historyStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const currentBillingSection = BILLING_SECTIONS.find(s => s.id === activeBillingSection) || BILLING_SECTIONS[0];
  const CurrentBillingIcon = currentBillingSection.icon;

  return (
    <div className="space-y-6 sm:space-y-8 max-w-6xl mx-auto pb-12">
      {/* Overview Banner Card */}
      <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-6 sm:p-8 relative overflow-hidden shadow-xl">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#14B8A6]/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative z-10 space-y-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 bg-[#14B8A6]/15 border border-[#14B8A6]/30 text-[#14B8A6] font-extrabold text-xs rounded-xl flex items-center gap-1.5 shadow-sm">
                  <Sparkles size={14} />
                  <span>CURRENT PLAN: {isTrialActive ? `SOVEREIGN FOUNDER PRO (${trialStatus.daysRemaining}D TRIAL)` : currentPlan.name.toUpperCase()}</span>
                </span>

                <span className={`px-2.5 py-1 rounded-xl text-xs font-bold flex items-center gap-1.5 border ${
                  isTrialActive 
                    ? 'bg-amber-500/15 border-amber-500/30 text-amber-300' 
                    : subData.status === 'active'
                    ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300' 
                    : 'bg-rose-500/15 border-rose-500/30 text-rose-300'
                }`}>
                  <span className={`w-2 h-2 rounded-full animate-pulse ${isTrialActive ? 'bg-amber-400' : subData.status === 'active' ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                  <span>{isTrialActive ? `${trialStatus.daysRemaining} DAYS REMAINING IN TRIAL` : subData.status.toUpperCase()}</span>
                </span>

                {isTrialActive ? (
                  <span className="text-xs text-amber-300/90 font-medium flex items-center gap-1.5 bg-amber-500/10 px-2.5 py-1 rounded-xl border border-amber-500/20">
                    <Clock size={12} className="text-amber-400 shrink-0" />
                    <span>Trial ends on {trialStatus.trialEndDate.split('T')[0]} (No auto-charge)</span>
                  </span>
                ) : subData.autoRenew && subData.status === 'active' ? (
                  <span className="text-xs text-gray-400 font-medium flex items-center gap-1">
                    <RefreshCw size={12} className="text-[#14B8A6]" />
                    <span>Auto-renews on {subData.nextBillingDate}</span>
                  </span>
                ) : null}
              </div>

              <h2 className="text-xl sm:text-2xl font-display font-bold text-white tracking-tight">
                Subscription Management &amp; Billing HQ
              </h2>
              <p className="text-xs text-gray-400 max-w-2xl leading-relaxed">
                Manage your active AzuraOS workspace tier, view detailed tax receipts, update payment options, and monitor allocated operational limits.
              </p>
            </div>

            {onNavigate && (
              <button
                type="button"
                onClick={() => onNavigate('/welcome')}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#0C0E0F] hover:bg-[#1A1D1E] border border-[#2A2F31] hover:border-[#14B8A6]/40 text-[#14B8A6] text-xs font-semibold transition-all cursor-pointer shadow-sm shrink-0 self-start md:self-auto"
              >
                <Sparkles size={13} />
                <span>Welcome &amp; BYOK Quickstart</span>
                <ArrowUpRight size={13} />
              </button>
            )}
          </div>

          {/* Action Command Bar: Billing Cycle & Manage Subscription firmly inside container */}
          <div className="pt-4 border-t border-[#1F2426] flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* Monthly / Yearly Billing Toggle */}
              <div className="bg-[#0C0E0F] p-1 border border-[#2A2F31] rounded-xl flex items-center shadow-inner shrink-0">
                <button
                  type="button"
                  onClick={() => setSelectedCycle('monthly')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                    selectedCycle === 'monthly'
                      ? 'bg-[#14B8A6] text-black shadow-md'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedCycle('yearly')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                    selectedCycle === 'yearly'
                      ? 'bg-[#14B8A6] text-black shadow-md'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <span>Yearly</span>
                  <span className={`px-1.5 py-0.5 text-[9px] font-extrabold uppercase rounded ${
                    selectedCycle === 'yearly' ? 'bg-black/20 text-black' : 'bg-amber-500/20 text-amber-300'
                  }`}>
                    Save 17%
                  </span>
                </button>
              </div>

              <button
                type="button"
                onClick={handleToggleAutoRenew}
                className={`px-3 py-1.5 rounded-xl border text-xs font-semibold transition flex items-center justify-center gap-1.5 cursor-pointer shrink-0 whitespace-nowrap ${
                  subData.autoRenew
                    ? 'bg-[#0C0E0F] border-[#2A2F31] text-gray-300 hover:text-white'
                    : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                }`}
              >
                <RefreshCw size={12} />
                <span>{subData.autoRenew ? 'Auto-Renew: ON' : 'Auto-Renew: OFF'}</span>
              </button>

              <button
                type="button"
                onClick={() => handleSyncWithDodo(true)}
                disabled={isSyncingDodo}
                className="px-3 py-1.5 rounded-xl bg-[#0C0E0F] hover:bg-[#1A1F21] border border-[#2A2F31] text-gray-200 hover:text-white text-xs font-semibold transition flex items-center justify-center gap-1.5 cursor-pointer shadow-sm disabled:opacity-50 shrink-0 whitespace-nowrap"
                title="Query Dodo Payments live API to refresh confirmed subscriptions and receipts"
              >
                <RefreshCw size={12} className={isSyncingDodo ? 'animate-spin text-[#14B8A6]' : 'text-[#14B8A6]'} />
                <span>{isSyncingDodo ? 'Syncing...' : 'Sync Status'}</span>
              </button>
            </div>

            {/* Primary Action Button (Manage or Upgrade) - Anchored inside card */}
            <div className="flex items-center gap-2">
              {subData.currentPlanId === 'starter' ? (
                <button
                  type="button"
                  onClick={() => {
                    setActiveBillingSection('plans');
                    const el = document.getElementById('subscription-plans-grid');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-4 py-2 rounded-xl bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs transition flex items-center justify-center gap-1.5 cursor-pointer shadow-md shadow-[#14B8A6]/20 shrink-0 whitespace-nowrap active:scale-95"
                  title="View available plans to upgrade"
                >
                  <Zap size={14} />
                  <span>Upgrade Plan</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleOpenManageSubscription}
                  disabled={isPortalLoading}
                  className="px-4 py-2 rounded-xl bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs transition flex items-center justify-center gap-1.5 cursor-pointer shadow-md shadow-[#14B8A6]/20 disabled:opacity-50 shrink-0 whitespace-nowrap active:scale-95"
                  title="Open Dodo Payments secure subscription management portal"
                >
                  {isPortalLoading ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
                  <span>Manage Subscription</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Quotas & Capacity Status Bar */}
        <div className="mt-6 pt-5 border-t border-[#1F2426] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="p-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1">
            <div className="text-[10px] text-gray-400 font-bold uppercase flex justify-between">
              <span>Client Invoices</span>
              <span className="text-[#14B8A6]">{currentPlan.maxInvoices === -1 ? 'Unlimited' : `${currentPlan.maxInvoices} / mo`}</span>
            </div>
            <div className="text-xs font-extrabold text-white">
              {currentPlan.maxInvoices === -1 ? 'Unlimited Capacity' : `Up to ${currentPlan.maxInvoices} / Month`}
            </div>
            <div className="w-full bg-[#1F2426] h-1.5 rounded-full overflow-hidden">
              <div className="bg-[#14B8A6] h-full w-full" />
            </div>
          </div>

          <div className="p-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1">
            <div className="text-[10px] text-gray-400 font-bold uppercase flex justify-between">
              <span>AI Strategic Queries</span>
              <span className="text-amber-400">
                {currentPlan.maxAiCredits === 0 
                  ? 'Pro Feature / BYOK' 
                  : currentPlan.maxAiCredits >= 10000 
                    ? 'Unlimited (BYOK)' 
                    : `${currentPlan.maxAiCredits} / day`}
              </span>
            </div>
            <div className="text-xs font-extrabold text-white">
              {currentPlan.maxAiCredits === 0 
                ? 'Requires Pro Upgrade or BYOK' 
                : currentPlan.maxAiCredits >= 10000 
                  ? 'Unlimited BYOK Active' 
                  : `${currentPlan.maxAiCredits} Requests / Day`}
            </div>
            <div className="w-full bg-[#1F2426] h-1.5 rounded-full overflow-hidden">
              <div className="bg-amber-400 h-full" style={{ width: currentPlan.maxAiCredits === 0 ? '0%' : '100%' }} />
            </div>
          </div>

          <div className="p-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1">
            <div className="text-[10px] text-gray-400 font-bold uppercase flex justify-between">
              <span>Founder Whitelist Seats</span>
              <span className="text-emerald-400">{currentPlan.maxTeamMembers} Max</span>
            </div>
            <div className="text-xs font-extrabold text-white">
              1 Active Founder Seat
            </div>
            <div className="w-full bg-[#1F2426] h-1.5 rounded-full overflow-hidden">
              <div className="bg-emerald-400 h-full" style={{ width: '20%' }} />
            </div>
          </div>

          <div className="p-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-1">
            <div className="text-[10px] text-gray-400 font-bold uppercase flex justify-between">
              <span>AES-256 Vault Backup</span>
              <span className="text-blue-400">{currentPlan.vaultStorage}</span>
            </div>
            <div className="text-xs font-extrabold text-white">
              Cloud Vault Active
            </div>
            <div className="w-full bg-[#1F2426] h-1.5 rounded-full overflow-hidden">
              <div className="bg-blue-400 h-full w-full" />
            </div>
          </div>
        </div>
      </div>

      {/* BILLING SECTION NAVIGATOR: DROPDOWN SWITCHER & COMPACT TABS */}
      <div className="relative z-20 space-y-3">
        {/* Active Section Header Card / Dropdown Trigger */}
        <div className="bg-[#151819] border border-[#2A2F31] rounded-2xl p-4 sm:p-5 shadow-lg flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-[#14B8A6]/15 border border-[#14B8A6]/30 flex items-center justify-center text-[#14B8A6] shrink-0 shadow-sm">
              <CurrentBillingIcon size={22} />
            </div>
            <div className="min-w-0 space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase font-mono font-bold tracking-wider text-[#14B8A6] bg-[#14B8A6]/10 px-2 py-0.5 rounded">
                  BILLING VIEW
                </span>
                {currentBillingSection.badge && (
                  <span className={`text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded border ${currentBillingSection.badgeColor || 'bg-gray-800 text-gray-300 border-gray-700'}`}>
                    {currentBillingSection.badge}
                  </span>
                )}
              </div>
              <h2 className="text-base sm:text-lg font-display font-bold text-white tracking-tight truncate">
                {currentBillingSection.title}
              </h2>
              <p className="text-xs text-gray-400 leading-relaxed max-w-2xl line-clamp-1 sm:line-clamp-none">
                {currentBillingSection.desc}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setIsSectionDropdownOpen(!isSectionDropdownOpen)}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-md border ${
                isSectionDropdownOpen
                  ? 'bg-[#14B8A6] text-black border-[#14B8A6]'
                  : 'bg-[#0C0E0F] hover:bg-[#1A1D1E] text-white border-[#2A2F31] hover:border-[#14B8A6]/50'
              }`}
              aria-expanded={isSectionDropdownOpen}
              aria-label="Switch Billing View"
            >
              <span>Switch View</span>
              <ChevronDown size={15} className={`transition-transform duration-200 ${isSectionDropdownOpen ? 'rotate-180 text-black' : 'text-[#14B8A6]'}`} />
            </button>
          </div>
        </div>

        {/* Dropdown Menu Panel with Descriptions */}
        {isSectionDropdownOpen && (
          <>
            <div 
              className="fixed inset-0 z-30 bg-black/50"
              onClick={() => setIsSectionDropdownOpen(false)}
            />
            <div className="absolute top-full left-0 right-0 mt-2 z-40 bg-[#0E1011] border border-[#2A2F31] rounded-2xl shadow-2xl overflow-hidden p-2.5 sm:p-3 animate-fade-in">
              <div className="px-3 py-2 border-b border-[#1F2426] flex items-center justify-between text-xs text-gray-400 font-medium">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Select Billing View</span>
                <span className="text-[10px] text-teal-400 font-mono">{BILLING_SECTIONS.length} Available Views</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 pt-2">
                {BILLING_SECTIONS.map((sec) => {
                  const Icon = sec.icon;
                  const isSelected = activeBillingSection === sec.id;
                  return (
                    <button
                      key={sec.id}
                      type="button"
                      onClick={() => {
                        setActiveBillingSection(sec.id);
                        setIsSectionDropdownOpen(false);
                        showToast(`💳 Switched to ${sec.shortLabel}`);
                      }}
                      className={`w-full text-left p-3 rounded-xl transition-all flex items-start gap-3 cursor-pointer border ${
                        isSelected
                          ? 'bg-[#14B8A6]/15 border-[#14B8A6]/40 text-white shadow-sm'
                          : 'bg-[#121516]/80 hover:bg-[#1A1E20] border-[#1F2426] hover:border-[#2A2F31] text-gray-300'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                        isSelected ? 'bg-[#14B8A6] text-black' : 'bg-[#181C1E] text-gray-400 border border-[#262E30]'
                      }`}>
                        <Icon size={15} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-xs font-bold truncate ${isSelected ? 'text-[#14B8A6]' : 'text-white'}`}>
                            {sec.title}
                          </span>
                          {sec.badge && (
                            <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded border shrink-0 ${sec.badgeColor || 'bg-gray-800 text-gray-300 border-gray-700'}`}>
                              {sec.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-400 leading-snug mt-0.5 line-clamp-2">
                          {sec.desc}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Clean Quick Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
          {BILLING_SECTIONS.map((sec) => {
            const Icon = sec.icon;
            const isSelected = activeBillingSection === sec.id;
            return (
              <button
                key={sec.id}
                type="button"
                onClick={() => setActiveBillingSection(sec.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all flex items-center gap-1.5 shrink-0 cursor-pointer border ${
                  isSelected
                    ? 'bg-[#14B8A6]/20 border-[#14B8A6]/40 text-[#14B8A6] font-bold shadow-xs'
                    : 'bg-[#121516] hover:bg-[#181C1E] border-[#1F2426] text-gray-400 hover:text-gray-200'
                }`}
                title={sec.desc}
              >
                <Icon size={13} className={isSelected ? 'text-[#14B8A6]' : 'text-gray-500'} />
                <span className="whitespace-nowrap">{sec.shortLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* VISUAL GUARANTEE & REFUND HELPER BANNER */}
      {(activeBillingSection === 'all' || activeBillingSection === 'quotas') && (
        <div className="bg-[#151819] border border-[#14B8A6]/30 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-gray-300 shadow-xl">
          <div className="flex items-center gap-3.5">
            <div className="p-2.5 rounded-xl bg-[#14B8A6]/15 text-[#14B8A6] border border-[#14B8A6]/30 shrink-0">
              <ShieldCheck size={20} />
            </div>
            <div>
              <strong className="text-white font-extrabold block text-sm">14-Day Risk-Free Guarantee &amp; Easy Cancellation</strong>
              <span className="font-medium text-gray-300 leading-relaxed">
                Try it first. If it’s not for you, you have 14 days to request a full refund. After that, cancel anytime.
              </span>
            </div>
          </div>
          {subData.currentPlanId === 'starter' ? (
            <button
              type="button"
              onClick={() => {
                setActiveBillingSection('plans');
                const el = document.getElementById('subscription-plans-grid');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-4 py-2 rounded-xl bg-[#0C0E0F] hover:bg-[#1A1D1E] text-[#14B8A6] border border-[#14B8A6]/40 font-extrabold text-xs shrink-0 transition flex items-center gap-1.5 cursor-pointer"
            >
              <Zap size={13} />
              <span>Explore Plans</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleOpenManageSubscription}
              disabled={isPortalLoading}
              className="px-4 py-2 rounded-xl bg-[#0C0E0F] hover:bg-[#1A1D1E] text-[#14B8A6] border border-[#14B8A6]/40 font-extrabold text-xs shrink-0 transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              {isPortalLoading ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
              <span>Manage Subscription</span>
            </button>
          )}
        </div>
      )}

      {/* SECTION 1: AVAILABLE PLAN TIERS & VALUE CALLOUT */}
      {(activeBillingSection === 'all' || activeBillingSection === 'plans') && (
        <div className="space-y-6">
          {/* 🚀 HIGH-CONVERTING VALUE & ROI CALLOUT */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#121B1C] via-[#161F21] to-[#0E1516] border border-[#14B8A6]/40 p-5 sm:p-6 shadow-xl text-gray-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-11 h-11 rounded-2xl bg-[#14B8A6]/15 border border-[#14B8A6]/30 text-[#14B8A6] flex items-center justify-center shrink-0 shadow-inner">
                <Sparkles size={22} className="text-[#14B8A6]" />
              </div>
              <div className="space-y-1">
                <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-[#14B8A6]/10 border border-[#14B8A6]/30 text-[#14B8A6] text-[10px] font-bold font-mono uppercase tracking-wider">
                  PROVEN FOUNDER VELOCITY
                </div>
                <p className="text-sm sm:text-base font-medium text-white leading-relaxed">
                  "Our users typically see a <span className="text-[#14B8A6] font-bold">3.4x increase</span> in focused deep-work execution and a <span className="text-[#14B8A6] font-bold">22% increase</span> in billable revenue collection within their first 7 days. At <span className="text-white font-bold">$29/mo</span>, you’re paying for a fraction of the value you'll generate in your first week."
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-[#151819] border border-[#1F2426] p-4 rounded-2xl">
              <div>
                <h3 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">
                  <Award size={18} className="text-[#14B8A6]" />
                  <span>Subscription Tier Matrix</span>
                </h3>
                <p className="text-xs text-gray-400">
                  Select or switch plans to scale your operational features and AI strategic capabilities.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {/* Auto-detected Currency Selector */}
                <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl px-3 py-1.5 flex items-center gap-2 shadow-inner">
                  <Globe size={14} className="text-[#14B8A6]" />
                  <span className="text-[10px] text-gray-400 font-extrabold uppercase">Currency:</span>
                  <select
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.target.value)}
                    className="bg-transparent text-white font-extrabold text-xs focus:outline-none cursor-pointer"
                  >
                    {Object.values(SUPPORTED_CURRENCIES).map((c) => (
                      <option key={c.code} value={c.code} className="bg-[#151819] text-white">
                        {c.flag} {c.code} ({c.symbol}) - {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="text-xs text-gray-400">
                  Billing Frequency: <strong className="text-teal-400 font-bold capitalize">{selectedCycle}</strong>
                </div>
              </div>
            </div>

            <div id="subscription-plans-grid" className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {AVAILABLE_PLANS.map((plan) => {
                const isCurrent = plan.id === subData.currentPlanId && !trialStatus.isTrialActive && (
                  plan.id === 'enterprise' || subData.billingCycle === selectedCycle
                );
                const isEnterprise = plan.id === 'enterprise';
                const priceInfo = getPlanPriceForCurrency(plan.id, selectedCycle, selectedCurrency);
                const periodText = selectedCycle === 'yearly' ? '/year' : '/month';

                return (
                  <div
                    key={plan.id}
                    className={`bg-[#151819] rounded-2xl p-6 relative flex flex-col justify-between transition-all shadow-xl ${
                      isCurrent
                        ? 'border-2 border-[#14B8A6] ring-1 ring-[#14B8A6]/30'
                        : plan.popular
                        ? 'border border-teal-500/40 hover:border-[#14B8A6]'
                        : 'border border-[#1F2426] hover:border-[#2A2F31]'
                    }`}
                  >
                    {plan.popular && !isCurrent && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-teal-500 to-[#14B8A6] text-black text-[10px] font-extrabold uppercase rounded-full shadow-md">
                        RECOMMENDED FOR SOLO OPERATORS
                      </div>
                    )}

                    {isCurrent && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#14B8A6] text-black text-[10px] font-extrabold uppercase rounded-full shadow-md flex items-center gap-1">
                        <CheckCircle2 size={12} />
                        <span>ACTIVE PRO PLAN</span>
                      </div>
                    )}

                    <div className="space-y-4 pt-2">
                      <div>
                        <h4 className="text-lg font-bold text-white">{plan.name}</h4>
                        <p className="text-xs text-gray-400 mt-1 min-h-[36px]">{plan.tagline}</p>
                      </div>

                      <div className="py-2 border-y border-[#1F2426]">
                        {isEnterprise ? (
                          <div>
                            <div className="text-2xl font-extrabold text-white">Custom Pricing</div>
                            <span className="text-[10px] text-[#14B8A6] font-bold block mt-1">
                              Tailored SLA, dedicated support &amp; custom capacity
                            </span>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-baseline gap-1">
                              <span className="text-3xl font-extrabold font-mono text-white">{priceInfo.formatted}</span>
                              <span className="text-xs text-gray-400 font-medium">{periodText}</span>
                            </div>
                            {selectedCycle === 'yearly' && priceInfo.amount > 0 && (
                              <span className="text-[10px] text-amber-400 font-bold block mt-1">
                                Equivalent to {activeCurrencyInfo.symbol}{Math.round(priceInfo.amount / 12)}/mo billed annually
                              </span>
                            )}
                          </>
                        )}
                      </div>

                      <div className="space-y-2.5">
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">
                          Included Features:
                        </span>
                        {plan.features.map((feat, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-xs text-gray-300">
                            <Check size={14} className="text-[#14B8A6] shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-6 mt-6 border-t border-[#1F2426] space-y-2">
                      {isCurrent ? (
                        <button
                          disabled
                          className="w-full py-3 bg-[#14B8A6]/20 border border-[#14B8A6]/40 text-[#14B8A6] font-bold text-xs rounded-xl cursor-default flex items-center justify-center gap-2"
                        >
                          <CheckCircle2 size={16} />
                          <span>Active Subscription</span>
                        </button>
                      ) : isEnterprise ? (
                        <a
                          href="mailto:enterprisesales@azuraos.app?subject=AzuraOS%20Enterprise%20Plan%20Inquiry"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full py-3 rounded-xl font-bold text-xs bg-[#14B8A6]/20 hover:bg-[#14B8A6]/30 text-[#14B8A6] border border-[#14B8A6]/40 transition shadow-lg flex items-center justify-center gap-2 cursor-pointer"
                        >
                          <Mail size={14} />
                          <span>Contact Enterprise Sales</span>
                        </a>
                      ) : (
                        <button
                          type="button"
                          disabled={isCheckoutLoading}
                          onClick={() => setSwitchingPlan(plan)}
                          className={`w-full py-3 rounded-xl font-bold text-xs transition shadow-lg flex items-center justify-center gap-2 cursor-pointer active:scale-95 ${
                            plan.popular
                              ? 'bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold'
                              : 'bg-[#0C0E0F] hover:bg-[#1A1D1E] border border-[#2A2F31] text-white'
                          }`}
                        >
                          {isCheckoutLoading ? (
                            <>
                              <Loader2 size={14} className="animate-spin text-current" />
                              <span>Connecting Dodo...</span>
                            </>
                          ) : (
                            <>
                              <span>{trialStatus.isTrialActive ? `Lock In ${plan.name}` : `Upgrade to ${plan.name}`}</span>
                              <ArrowUpRight size={14} />
                            </>
                          )}
                        </button>
                      )}

                      {/* STATUTORY AUTO-RENEWAL & CANCELLATION DISCLOSURE (FTC & CAL. ARL COMPLIANT) */}
                      {!isCurrent && !isEnterprise && priceInfo.amount > 0 && (
                        <div className="p-2.5 rounded-xl bg-[#0C0E0F]/80 border border-[#222B2E] text-[10px] text-gray-400 space-y-1">
                          <p className="text-gray-300 leading-tight">
                            <strong className="text-white">Renewal Terms:</strong> Billed {selectedCycle === 'yearly' ? 'annually' : 'monthly'}. Automatically renews at {priceInfo.formatted}/{periodText} until cancelled.
                          </p>
                          <p className="text-gray-400 leading-tight">
                            Cancel anytime in 1-click in Account Settings or Dodo Customer Portal. 14-day money-back guarantee.
                          </p>
                        </div>
                      )}

                      <div className="text-[10px] text-gray-500 text-center font-medium flex items-center justify-center gap-1">
                        <ShieldCheck size={12} className="text-emerald-400" />
                        <span>Processed via Dodo Payments (Google Pay &amp; Cards)</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* PROMINENT SOVEREIGN BYOK AI & GOOGLE CLOUD BILLING DISCLAIMER */}
            <div className="p-4 sm:p-5 bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-gray-300">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center shrink-0 mt-0.5">
                  <ShieldAlert size={18} />
                </div>
                <div className="space-y-1">
                  <h4 className="font-bold text-white text-xs flex items-center gap-2">
                    <span>AI Co-Founder &amp; Concierge BYOK Sovereign Architecture Disclaimer</span>
                    <span className="px-2 py-0.5 bg-[#14B8A6]/20 text-[#14B8A6] rounded text-[9px] font-mono font-bold uppercase">
                      Zero AI Markups
                    </span>
                  </h4>
                  <p className="text-gray-400 text-[11px] leading-relaxed">
                    AzuraOS utilizes a sovereign Bring-Your-Own-Key (BYOK) architecture for the AI Co-Founder and Concierge. AI requests stream directly between your browser and Google AI Studio endpoints. AzuraOS does not resell AI tokens, levy hidden API surcharges, or manage third-party quotas. 
                    <strong className="text-gray-200"> You are solely responsible for connecting your own Google AI Studio API key and for any usage fees, rate limits, tier upgrades, or surcharges incurred through your personal or organizational Google Cloud billing account.</strong>
                  </p>
                </div>
              </div>
              <a
                href="https://aistudio.google.com/app/apikey"
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 px-3.5 py-2 bg-[#151819] hover:bg-[#1A1D1E] text-teal-400 hover:text-white border border-[#2A2F31] hover:border-teal-500/40 rounded-xl font-bold text-[11px] transition flex items-center gap-1.5 shadow-sm"
              >
                <span>Google AI Studio Keys</span>
                <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* SECTION 2: BILLING HISTORY & RECEIPTS */}
      {(activeBillingSection === 'all' || activeBillingSection === 'receipts') && (
        <div className="bg-[#151819] border border-[#1F2426] rounded-2xl p-6 shadow-xl space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#1F2426] pb-4">
            <div>
              <h3 className="text-sm font-bold text-white uppercase tracking-wide flex items-center gap-2">
                <FileText size={16} className="text-[#14B8A6]" />
                <span>Subscription Billing History &amp; Receipts ({subData.history.length})</span>
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                Official tax receipts for all recurring workspace charges and plan upgrades.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExportBillingHistoryCSV}
                className="px-3.5 py-2 bg-[#0C0E0F] hover:bg-[#1A1D1E] border border-[#2A2F31] text-teal-400 text-xs font-bold rounded-xl transition flex items-center gap-1.5 cursor-pointer"
              >
                <Download size={14} />
                <span>Export History CSV</span>
              </button>
            </div>
          </div>

        {/* Toolbar Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="relative w-full sm:w-64">
            <Search size={14} className="absolute left-3 top-3 text-gray-500" />
            <input
              type="text"
              placeholder="Search invoice # or plan..."
              value={historySearchQuery}
              onChange={(e) => setHistorySearchQuery(e.target.value)}
              className="w-full bg-[#0C0E0F] border border-[#2A2F31] rounded-xl pl-8 pr-3 py-2 text-xs text-white placeholder:text-gray-500 focus:outline-none focus:border-[#14B8A6]"
            />
          </div>

          <div className="flex items-center gap-1.5">
            {['all', 'paid', 'refunded', 'pending'].map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => setHistoryStatusFilter(st)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold capitalize transition cursor-pointer ${
                  historyStatusFilter === st
                    ? 'bg-[#14B8A6]/20 text-[#14B8A6] border border-[#14B8A6]/40'
                    : 'bg-[#0C0E0F] text-gray-400 border border-[#2A2F31]'
                }`}
              >
                {st}
              </button>
            ))}
          </div>
        </div>

        {/* History Table */}
        <div className="overflow-x-auto border border-[#2A2F31] rounded-xl">
          <table className="w-full text-left border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-[#0C0E0F] border-b border-[#2A2F31] text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                <th className="py-3 px-4">Invoice #</th>
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Description</th>
                <th className="py-3 px-4">Amount</th>
                <th className="py-3 px-4">Payment Method</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Receipt Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1F2426] text-xs">
              {filteredHistory.map((item) => (
                <tr key={item.id} className="hover:bg-[#0C0E0F]/50 transition">
                  <td className="py-3.5 px-4 font-mono font-bold text-white">
                    {item.invoiceNumber}
                  </td>
                  <td className="py-3.5 px-4 text-gray-400 font-mono">
                    {item.date}
                  </td>
                  <td className="py-3.5 px-4 text-gray-200 font-medium">
                    {item.description}
                  </td>
                  <td className="py-3.5 px-4 font-mono font-bold text-[#14B8A6]">
                    ${item.amount.toFixed(2)} {item.currency}
                  </td>
                  <td className="py-3.5 px-4 text-gray-400 font-mono text-[11px]">
                    <span className="px-2 py-0.5 bg-[#0C0E0F] border border-[#2A2F31] rounded text-gray-300 font-sans text-[10px]">Secured Checkout</span>
                  </td>
                  <td className="py-3.5 px-4">
                    <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase rounded border bg-emerald-500/15 border-emerald-500/30 text-emerald-300">
                      {item.status}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-right">
                    <button
                      type="button"
                      onClick={() => downloadSubscriptionInvoice(item, {
                        name: getEffectiveCustomerName(),
                        email: subData.billingEmail || userEmail || '',
                        taxId: subData.billingTaxId || getBusinessSettings().taxNumber || '',
                        address: subData.billingAddress || getBusinessSettings().address || '',
                      })}
                      className="px-3 py-1.5 bg-[#0C0E0F] hover:bg-[#1A1D1E] border border-[#2A2F31] hover:border-[#14B8A6]/40 text-teal-400 rounded-lg text-xs font-bold transition inline-flex items-center gap-1 cursor-pointer"
                    >
                      <Download size={13} />
                      <span>Receipt</span>
                    </button>
                  </td>
                </tr>
              ))}

              {filteredHistory.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500 text-xs">
                    No matching billing history entries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* PLAN SWITCH CONFIRMATION & DODO CHECKOUT MODAL */}
      {switchingPlan && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-[#151819] border border-[#2A2F31] rounded-2xl max-w-md w-full p-6 space-y-5 shadow-2xl relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#14B8A6]/20 border border-[#14B8A6]/40 text-[#14B8A6] flex items-center justify-center shrink-0">
                  <Sparkles size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Confirm Plan Activation</h3>
                  <p className="text-xs text-gray-400">Dodo Payments Merchant of Record</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSwitchingPlan(null)}
                className="text-gray-400 hover:text-white p-1 rounded-lg transition"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-3 text-xs">
              <div className="flex justify-between items-center border-b border-[#1F2426] pb-2">
                <span className="text-gray-400 font-bold uppercase text-[10px]">Target Plan</span>
                <span className="text-white font-bold">{switchingPlan.name}</span>
              </div>

              <div className="flex justify-between items-center border-b border-[#1F2426] pb-2">
                <span className="text-gray-400 font-bold uppercase text-[10px]">Billing Cycle</span>
                <span className="text-teal-400 font-bold uppercase">{selectedCycle}</span>
              </div>

              <div className="flex justify-between items-center border-b border-[#1F2426] pb-2">
                <span className="text-gray-400 font-bold uppercase text-[10px]">Total Amount ({selectedCurrency})</span>
                <span className="text-white font-mono font-bold text-sm">
                  {getPlanPriceForCurrency(switchingPlan.id, selectedCycle, selectedCurrency).formatted} {selectedCurrency}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-400 font-bold uppercase text-[10px]">Payment Rail</span>
                <span className="text-emerald-400 font-mono font-bold flex items-center gap-1">
                  <ShieldCheck size={12} />
                  {switchingPlan.id === 'starter' || switchingPlan.monthlyPrice === 0 ? 'Direct Activation (Free)' : 'Dodo Google Pay & Cards'}
                </span>
              </div>
            </div>

            <div className="p-3 bg-[#14B8A6]/10 border border-[#14B8A6]/30 text-teal-300 rounded-xl text-[11px] leading-relaxed flex items-start gap-2">
              <ShieldCheck size={16} className="shrink-0 mt-0.5 text-[#14B8A6]" />
              <div>
                {switchingPlan.id === 'starter' || switchingPlan.monthlyPrice === 0 ? (
                  <><strong>Free Tier Switch:</strong> No payment method or credit card required. Account limits updated immediately.</>
                ) : (
                  <><strong>PCI-DSS Compliant Checkout:</strong> Clicking below opens Dodo Payments overlay modal. AzuraOS never stores raw card details.</>
                )}
              </div>
            </div>

            {/* STATUTORY AUTO-RENEWAL & CANCELLATION DISCLOSURE (FTC CLICK-TO-CANCEL & CAL. ARL) */}
            {switchingPlan.monthlyPrice > 0 ? (
              <div className="p-3 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl text-[11px] text-gray-400 space-y-1">
                <p className="text-gray-300">
                  <strong className="text-white">Statutory Auto-Renewal Terms:</strong> By clicking "Proceed to Dodo Checkout", you authorize recurring charges of {getPlanPriceForCurrency(switchingPlan.id, selectedCycle, selectedCurrency).formatted} {selectedCycle === 'yearly' ? 'annually' : 'monthly'} until cancelled.
                </p>
                <p className="text-gray-400">
                  Cancel anytime in 1-click in Account Settings or the Dodo customer portal. 14-day full refund guarantee covers initial purchase and renewals.
                </p>
              </div>
            ) : (
              <div className="pt-2 text-[11px] text-gray-400 text-center flex items-center justify-center gap-1.5 leading-snug">
                <ShieldCheck size={14} className="text-[#14B8A6] shrink-0" />
                <span>Free tier is always free. No credit card required. Upgrade anytime.</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                disabled={isCheckoutLoading}
                onClick={() => setSwitchingPlan(null)}
                className="px-4 py-2.5 bg-[#0C0E0F] hover:bg-[#1A1D1E] text-gray-400 hover:text-white border border-[#2A2F31] rounded-xl text-xs font-bold transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isCheckoutLoading}
                onClick={handleConfirmPlanSwitch}
                className="px-5 py-2.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl shadow-lg transition cursor-pointer flex items-center gap-2"
              >
                {isCheckoutLoading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>Switching Plan...</span>
                  </>
                ) : (
                  <>
                    <span>{switchingPlan.id === 'starter' || switchingPlan.monthlyPrice === 0 ? 'Confirm Switch to Free Tier' : 'Proceed to Dodo Checkout'}</span>
                    <ArrowUpRight size={14} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DODO CHECKOUT MODAL (LIVE & TEST MODES) */}
      {activeCheckoutSession && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-[#151819] border border-[#14B8A6]/40 rounded-2xl max-w-lg w-full p-6 space-y-5 shadow-2xl relative">
            <div className="flex items-center justify-between border-b border-[#2A2F31] pb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-[#14B8A6]/20 border border-[#14B8A6]/40 text-[#14B8A6] flex items-center justify-center font-extrabold text-sm">
                  🦤
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-white">
                      {activeCheckoutSession.isLive ? 'Dodo Payments Live Checkout' : 'Dodo Payments Test Checkout'}
                    </h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                      activeCheckoutSession.isLive 
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    }`}>
                      {activeCheckoutSession.isLive ? 'Live Mode' : 'Test Mode'}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-400">
                    {activeCheckoutSession.isLive 
                      ? 'Secure Merchant Payment Gateway' 
                      : 'Developer Testing & Simulation Sandbox'}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setActiveCheckoutSession(null)}
                className="text-gray-400 hover:text-white p-1 rounded-lg transition"
              >
                <X size={18} />
              </button>
            </div>

            {dodoNotice && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-xl text-xs">
                ℹ️ {dodoNotice}
              </div>
            )}

            <div className="p-4 bg-[#0C0E0F] border border-[#2A2F31] rounded-xl space-y-2.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">Target Plan:</span>
                <span className="font-bold text-white">{activeCheckoutSession.plan?.name || switchingPlan?.name || 'Sovereign Pro'}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">Billing Cycle:</span>
                <span className="font-semibold text-teal-300 capitalize">{selectedCycle}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">Amount:</span>
                <span className="font-bold text-white">
                  {selectedCurrency} {activeCheckoutSession.plan ? getPlanPriceForCurrency(activeCheckoutSession.plan.id, selectedCycle, selectedCurrency).amount : '0.00'}
                </span>
              </div>
              <div className="pt-2 border-t border-[#1C2022] text-[10px] font-mono text-gray-500 truncate">
                Session: {activeCheckoutSession.url}
              </div>
            </div>

            {/* LIVE MODE INSTRUCTIONS */}
            {activeCheckoutSession.isLive ? (
              <div className="p-3.5 bg-emerald-500/10 border border-emerald-500/25 rounded-xl text-xs text-emerald-300/90 space-y-1.5 leading-relaxed">
                <div className="flex items-center gap-1.5 font-semibold text-emerald-300">
                  <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
                  <span>Live Checkout Window Opened</span>
                </div>
                <p className="text-[11px] text-gray-300">
                  Please complete your card details securely on the Dodo Payments hosted payment page. If the window did not open automatically, click <strong>"Open Checkout Window"</strong> below.
                </p>
              </div>
            ) : (
              <div className="p-3.5 bg-amber-500/10 border border-amber-500/25 rounded-xl text-xs text-amber-300/90 space-y-1.5 leading-relaxed">
                <div className="flex items-center gap-1.5 font-semibold text-amber-300">
                  <AlertTriangle size={14} className="text-amber-400 shrink-0" />
                  <span>Sandbox Test Mode Active</span>
                </div>
                <p className="text-[11px] text-gray-300">
                  You are currently testing in sandbox mode. To switch to live mode, provide your live Dodo Payments API key (<code className="text-teal-300 font-mono">DODO_PAYMENTS_API_KEY</code>) and set <code className="text-teal-300 font-mono">DODO_PAYMENTS_ENVIRONMENT=live</code> in your settings.
                </p>
              </div>
            )}

            {/* CHECKOUT FOOTER REFUND HELPER TEXT */}
            <div className="pt-2 border-t border-[#2A2F31] text-[11px] text-gray-300 text-center flex items-center justify-center gap-1.5 leading-snug font-medium">
              <ShieldCheck size={14} className="text-[#14B8A6] shrink-0" />
              <span>Try it first. If it’s not for you, you have 14 days to request a full refund. After that, cancel anytime.</span>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-2">
              <a
                href={activeCheckoutSession.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-4 py-2.5 bg-[#0C0E0F] hover:bg-[#1A1D1E] text-white border border-[#2A2F31] rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5"
              >
                <span>{activeCheckoutSession.isLive ? 'Open Checkout Window' : 'Open in Dodo Test Page'}</span>
                <ExternalLink size={13} />
              </a>

              {activeCheckoutSession.isLive ? (
                <button
                  type="button"
                  onClick={() => {
                    const targetPlan = activeCheckoutSession.plan || switchingPlan;
                    if (targetPlan) {
                      const priceInfo = getPlanPriceForCurrency(targetPlan.id, selectedCycle, selectedCurrency);
                      completeSubscriptionActivation(targetPlan, priceInfo.amount, {
                        payment_id: `dodo_live_${Date.now()}`,
                        payment_method: { last4: 'CARD' }
                      });
                      setActiveCheckoutSession(null);
                    }
                  }}
                  className="w-full sm:w-auto px-5 py-2.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl shadow-lg transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <CheckCircle2 size={14} />
                  <span>I Have Completed Payment</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    const targetPlan = activeCheckoutSession.plan || switchingPlan;
                    if (targetPlan) {
                      const priceInfo = getPlanPriceForCurrency(targetPlan.id, selectedCycle, selectedCurrency);
                      completeSubscriptionActivation(targetPlan, priceInfo.amount, {
                        payment_id: `dodo_test_${Date.now()}`,
                        payment_method: { last4: '4242' }
                      });
                      setActiveCheckoutSession(null);
                    }
                  }}
                  className="w-full sm:w-auto px-5 py-2.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs rounded-xl shadow-lg transition flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <CheckCircle2 size={14} />
                  <span>Simulate Payment Success</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const SubscriptionBillingView = React.memo(SubscriptionBillingViewComponent);
